// TAS-178: pure decision logic for the session_live_state reconcile.
//
// Kept side-effect-free (no React, no Supabase) so the correctness-critical
// guards — forward-only, monotonicity, bounds, phase agreement — are unit-tested
// in isolation. GameFlowProvider's reconcile effect calls this, then performs the
// prefetch + setStepIndex if `applyStepIndex` is non-null.
//
// Part 2 (Lane F) adds two inputs and one output:
//
//   - `authoritative`: the row came back from this phone's OWN advance_game_step
//     / restart_question / help-sheet call (spec §5, H2). Such a row may move
//     the phone BACKWARDS within a run: in game 8827 the GM's phone sat on 38
//     through a legacy event while the server stayed on 37, and every Next was
//     refused. An authoritative row at the phone's own version counts as new
//     (the legacy event paths never bank a version, so the server's row carries
//     the version the phone already holds). The caller is responsible for the
//     N2 staleness guard (same run, version >= local) before passing it here.
//   - `localAttempts`: the attempt this phone last saw per round id. When a row
//     for the SAME run and the SAME step carries a higher `payload.attempt` than
//     the phone recorded, `attemptChanged` is raised: the host restarted the
//     question the phone is on. A round the phone has no record of never raises
//     (a fresh mount mid-attempt is not a restart it witnessed).

/**
 * Round id + attempt a live row names, or nulls. Legacy writers leave both out;
 * the contract says to treat a missing attempt as 0.
 * @param {object|null} row
 * @returns {{ roundId: string|null, attempt: number|null }}
 */
export function liveRowRound(row) {
  const payload = row && typeof row.payload === 'object' && row.payload ? row.payload : null;
  const roundId = payload && typeof payload.round_id === 'string' && payload.round_id ? payload.round_id : null;
  if (!roundId) return { roundId: null, attempt: null };
  const attempt = Number.isInteger(payload.attempt) ? payload.attempt : 0;
  return { roundId, attempt };
}

/**
 * @param {object|null} row  the session_live_state row: { run_id, version, step_index, phase, payload? }
 * @param {object} ctx
 * @param {string|null} ctx.localRunId       our last-seen run_id
 * @param {number}      ctx.localVersion      our last-seen version
 * @param {Array}       ctx.steps             the loaded flowSteps
 * @param {number}      ctx.currentStepIndex  our current step pointer
 * @param {boolean}     [ctx.authoritative]   row returned by our own step/restart RPC (H2)
 * @param {object}      [ctx.localAttempts]   { [roundId]: attempt } this phone has recorded
 * @returns {{ runId: string|null, version: number, applyStepIndex: number|null, resetToRun: boolean, attemptChanged: boolean }}
 *   `runId`/`version` = our updated monotonicity view (always advance it);
 *   `applyStepIndex` = the step to move to, or null to do nothing;
 *   `resetToRun` = true when this apply is a new-run epoch reset (may move BACKWARD),
 *   so the caller can skip its forward-only post-async re-check for this one;
 *   `attemptChanged` = the round on the current step was restarted.
 */
export function decideLiveStateApply(row, {
  localRunId,
  localVersion,
  steps,
  currentStepIndex,
  authoritative = false,
  localAttempts = null,
}) {
  const noop = { runId: localRunId, version: localVersion, applyStepIndex: null, resetToRun: false, attemptChanged: false };
  if (!row) return noop;

  const runChanged = Boolean(row.run_id) && row.run_id !== localRunId;
  const hasVersion = typeof row.version === 'number';
  // An authoritative row at our own version is still news: the phone can be
  // AHEAD of it only through a legacy event path that never banked a version.
  const isNew = runChanged
    || (hasVersion && row.version > localVersion)
    || (authoritative && hasVersion && row.version >= localVersion);
  if (!isNew) return noop;

  // Do NOT advance our (run_id, version) view until flowSteps are loaded and we
  // can actually evaluate the row. Otherwise a read that arrives before flowSteps
  // load would mark this row "seen" and the reconcile would never apply it once
  // steps arrive (a later re-read/subscription event re-evaluates instead).
  if (!Array.isArray(steps) || steps.length === 0) return noop;

  // Advance the monotonicity view: a new run adopts its version fully (session
  // reset), otherwise take the max so we never move backward.
  const runId = row.run_id || localRunId;
  let version = localVersion;
  if (runChanged && hasVersion) version = row.version;
  else if (hasVersion && row.version > localVersion) version = row.version;

  const out = { runId, version, applyStepIndex: null, resetToRun: runChanged, attemptChanged: false };
  const idx = row.step_index;

  // Restart detection (H5): same run, same step, a higher attempt for a round
  // this phone has already recorded.
  if (!runChanged && Number.isInteger(idx) && idx === currentStepIndex) {
    const { roundId, attempt } = liveRowRound(row);
    const known = roundId && localAttempts ? localAttempts[roundId] : undefined;
    if (roundId && Number.isInteger(known) && attempt > known) out.attemptChanged = true;
  }

  if (!Number.isInteger(idx) || idx < 0 || idx >= steps.length) return out; // bounds
  // Forward-only WITHIN a run — a slow/stale row must never rewind live play.
  // A run_id change is an epoch reset (the GM restarted the session), which
  // legitimately rewinds (e.g. back to the lobby at step 0), so we adopt its
  // step even when it's backward from where we are (TAS-181). An authoritative
  // row may rewind too (H2), but a row for the step we are already on moves
  // nothing.
  if (!runChanged && !authoritative && idx <= currentStepIndex) return out;
  if (!runChanged && authoritative && idx === currentStepIndex) return out;
  if (row.phase && steps[idx] && steps[idx].type && row.phase !== steps[idx].type) return out; // phase must agree with our flow

  out.applyStepIndex = idx;
  return out;
}
